import { useCallback, type ReactNode } from "react";
import { Box } from "@chakra-ui/react";

/**
 * Make a selection copied out of a table paste as a table.
 *
 * The browser copies the HTML it finds, and our cells stack several elements --
 * a name above a short name, a unit above its code and type. Every one of those
 * is a block, so a spreadsheet reads each as its own row and a two-row selection
 * arrives as a dozen. Plain text is worse still: no structure at all.
 *
 * So the copy is rebuilt from the rendered rows instead. One line per row, one
 * tab-separated field per column, with the stacked lines inside a cell joined by
 * a space -- readable as text, and parsed as a grid by a spreadsheet.
 *
 * Both flavours are written: `text/html` because Excel and Sheets prefer it and
 * it keeps cell boundaries exactly, and `text/plain` as the TSV fallback that
 * Notepad and any terminal can read.
 */

/** A cell's visible text, with its internal line breaks flattened. */
function cellText(cell: Element): string {
  const text = (cell as HTMLElement).innerText ?? cell.textContent ?? "";
  return text
    .replace(/\s*\n\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function buildRows(container: HTMLElement, range: Range): string[][] {
  const rows: string[][] = [];

  // Headers only when the selection actually reaches them, so copying a couple
  // of body rows does not silently gain a header line.
  const headerRow = container.querySelector("thead tr");
  if (headerRow && range.intersectsNode(headerRow)) {
    rows.push([...headerRow.querySelectorAll("th")].map(cellText));
  }

  for (const row of container.querySelectorAll("tbody tr")) {
    if (!range.intersectsNode(row)) {
      continue;
    }
    const cells = [...row.querySelectorAll("td")].map(cellText);
    // Skip the "no records" placeholder and any other single-cell filler.
    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  return rows;
}

export function CopyableTable({ children }: { children: ReactNode }) {
  const handleCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const container = event.currentTarget;
    const range = selection.getRangeAt(0);
    const rows = buildRows(container, range);

    // Nothing recognisable as rows -- a caption, a stray label -- so leave the
    // browser's own copy alone rather than replacing it with nothing.
    if (rows.length === 0) {
      return;
    }

    const tsv = rows.map((cells) => cells.join("\t")).join("\n");
    const html =
      "<table>" +
      rows
        .map(
          (cells) =>
            "<tr>" + cells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("") + "</tr>",
        )
        .join("") +
      "</table>";

    event.clipboardData.setData("text/plain", tsv);
    event.clipboardData.setData("text/html", html);
    event.preventDefault();
  }, []);

  return <Box onCopy={handleCopy}>{children}</Box>;
}
